import { z } from 'zod';
/** Governance — filings, equity, board, HR, compliance, insurance.
 *  Multi-tenant; orgMember read, orgAdmin ack/snooze, server-only write. */
export const GOVERNANCE_ITEMS_COLLECTION = 'governanceItems';
/** Drives the icon + filing-method block on the detail page. */
export const GovernanceCategory = z.enum([
    'filing', // franchise tax, Form D, state registrations
    'equity', // UWC, grant approvals, 409A
    'board', // board meetings, board packages, written consents
    'hr', // payroll registration in new states, EOY 1099/W-2
    'compliance', // SOC2, GDPR, data-residency audits
    'insurance', // D&O renewal, E&O, cyber
]);
/** Lifecycle pills on /governance. */
export const GovernanceStatus = z.enum([
    'overdue', // due date passed, action required
    'due', // due within the current week
    'upcoming', // ≥1 week out; agent watching but no action needed
    'in-progress', // operator opened the item; the draft is being edited
    'agent-ready', // agent has prepped a complete draft awaiting approval
    'done', // filed/paid/approved + receipts attached
]);
/** `uid: null` when the item has no assignee yet. */
export const GovernanceOwner = z.object({
    uid: z.string().nullable(),
    displayName: z.string(),
    role: z.enum(['CEO', 'CFO', 'COO', 'People Ops', 'Legal', 'External Counsel', 'Other']),
});
/** Integration affordance ("Carta auto-files", "Gusto auto-registers"). */
export const GovernanceAction = z.object({
    label: z.string(),
    /** Connector slug, or null for purely human actions (e.g. "Sign UWC"). */
    connector: z.string().nullable(),
    /** Agent fires without operator approval. Defaults false (rehearsal-gated). */
    autonomous: z.boolean().default(false),
});
/** Powers `/governance` (timeline), `/governance/$itemId` (detail),
 *  `/governance/calendar`, and the agent's escalation logic. */
export const GovernanceItem = z.object({
    id: z.string(),
    orgId: z.string(),
    category: GovernanceCategory,
    status: GovernanceStatus,
    title: z.string().max(160),
    subtitle: z.string().max(240).optional(),
    owner: GovernanceOwner,
    /** ISO 8601 YYYY-MM-DD. */
    dueDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'expected YYYY-MM-DD'),
    /** Null for items with no filing fee (e.g. board meetings). */
    estimatedCostUsdCents: z.number().int().nullable(),
    stageGate: z
        .enum(['pre-seed', 'seed', 'series-a-plus', 'series-b-plus', 'series-c-plus', 'any'])
        .default('any'),
    /** First `autonomous: true` action becomes the default Approve button. */
    actions: z.array(GovernanceAction).min(1),
    /** ISO 8601 YYYY-MM-DD — used for "watching since" rail copy. */
    watchingSince: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
});
/** Quarterly board package — typed sections, each with its own provenance. */
export const BoardPackageSection = z.object({
    kind: z.enum([
        'cover-memo',
        'scorecard',
        'cash-burn-runway',
        'rocks',
        'cohorts-arr',
        'gross-margin',
        'option-grants',
        'ask',
    ]),
    title: z.string(),
    /** `live:<route>` reflects current numbers; `draft:<authorId>` is human-authored. */
    source: z.string(),
    body: z.string(),
});
export const BoardPackage = z.object({
    id: z.string(),
    orgId: z.string(),
    quarterKey: z.string().regex(/^\d{4}-Q[1-4]$/),
    meetingDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
    status: z.enum(['drafted', 'in-review', 'final', 'sent']),
    sections: z.array(BoardPackageSection),
});
/** Provider listing for the "Get help" marketplace screen. */
export const GovernanceProvider = z.object({
    id: z.string(),
    name: z.string(),
    kind: z.enum(['law-firm', 'tax-cpa', 'broker', 'fractional-cfo', 'pe-counsel']),
    blurb: z.string(),
    vetting: z.enum(['leanopx-vetted', 'community-recommended', 'partner']),
    rateUsdCentsPerHour: z.number().int().nullable(),
});
